// src/screens/WhatTodayChanged.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Close Screen 2 — "What Today Changed"
// Brief 13 Section 6: Tier 1 (app-calculated) always visible,
// Tier 2 (Voyant-confirmed) shown when available.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import Scoreboard from '../components/Scoreboard';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function WhatTodayChanged({ data }: Props) {
  // ── Tally values (Tier 1 — always available) ─────────────────────────────
  const taxSavedPerYear = data.totalTaxSavedAnnual || data.planTaxSavedPerYear || 0;
  const cumulativeTaxSaved = data.totalTaxSavedCumulative || data.planCumulativeTaxSaved || 0;
  const mortgageInterestSaved = data.totalInterestSaved || data.planMortgageInterestSaved || 0;
  const totalCostsAvoided = cumulativeTaxSaved + mortgageInterestSaved;

  // Monthly committed (sum of lever amounts)
  const monthlyCommitted =
    (data.planDividendReduction || 0) +
    (data.planMortgageOverpayment || 0) +
    (data.planEfMonthlyTopUp || 0) +
    (data.planIsaMonthlyContribution || 0);

  // ── Voyant values (Tier 2 — only when confirmed) ─────────────────────────
  const doNothingRetirementAge = data.voyantDoNothing?.retirementAge || 0;
  const doNothingPot = data.voyantDoNothing?.potAtRetirement || 0;
  const doNothingAnnualSavings = data.voyantDoNothing?.voyantAnnualSavingsNeeded || 0;
  const hasDoNothing = doNothingRetirementAge > 0;

  const s1RetirementAge = data.voyantS1RetirementAge || 0;
  const s1Pot = data.voyantS1PotAtTargetAge || 0;
  const s1AnnualSavings = data.voyantS1AnnualSavingsNeeded || 0;
  const s1Captured = data.voyantS1Captured;
  const hasVoyantComparison = s1Captured && hasDoNothing;

  const retirementAgeDelta = doNothingRetirementAge - s1RetirementAge;
  const potDelta = s1Pot - doNothingPot;
  const lumpSumDelta = doNothingAnnualSavings - s1AnnualSavings;

  // ── Session 2 target ───────────────────────────────────────────────────
  const monthlyGap = s1AnnualSavings > 0 ? Math.round(s1AnnualSavings / 12) : 0;
  const freedomAge = data.targetFreedomAge || 55;
  const businessMRR = data.businessRevenueMRR || 0;

  return (
    <div className="flex flex-col h-full">
      <Scoreboard data={data} />

      <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-ma-navy">What Today Changed</h2>
          <p className="text-gray-500 mt-1 text-sm">The difference between where you were and where you are now.</p>
        </div>

        {/* ── TIER 1 — App-calculated, ALWAYS VISIBLE ─────────────────────── */}

        {/* Total costs avoided — gold headline */}
        <div className="rounded-2xl p-6 mb-6 text-center"
             style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)' }}>
          <p className="text-white/60 text-sm mb-2">Today saved you</p>
          <p className="font-bold" style={{ color: '#C9A84C', fontSize: '72px' }}>
            {fmt(totalCostsAvoided)}
          </p>
          <p className="text-white/50 text-xs mt-2">in tax and interest</p>
          {totalCostsAvoided > 3500 && (
            <p style={{ color: '#64DAD2', fontSize: '16px', marginTop: '8px' }}>
              That's {Math.round(totalCostsAvoided / 3500)}× the cost of today's session
            </p>
          )}
        </div>

        {/* Tier 1 detail cards */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">
            Your Decisions Today
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-700">Tax saved this year</span>
              <span className="text-sm font-bold text-green-600">{fmt(taxSavedPerYear)}/year</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-700">Tax saved to retirement</span>
              <span className="text-sm font-bold text-green-600">{fmt(cumulativeTaxSaved)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-700">Mortgage interest saved</span>
              <span className="text-sm font-bold text-green-600">{fmt(mortgageInterestSaved)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700">Monthly committed (new SOs)</span>
              <span className="text-sm font-bold text-ma-navy">{fmt(monthlyCommitted)}/month</span>
            </div>
          </div>
        </div>

        {/* ── TIER 2 — Voyant-confirmed ───────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">
            Before vs After
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 text-gray-500 font-medium w-1/3"></th>
                  <th className="text-right py-3 text-gray-500 font-medium">Before</th>
                  <th className="text-right py-3 text-gray-500 font-medium">After</th>
                  <th className="text-right py-3 text-gray-500 font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {/* Tier 1 rows — always show */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-700 font-medium">Tax saved this year</td>
                  <td className="py-3 text-right text-gray-600">£0</td>
                  <td className="py-3 text-right font-bold text-green-600">{fmt(taxSavedPerYear)}</td>
                  <td className="py-3 text-right">
                    <span className="text-green-600 font-bold">+{fmt(taxSavedPerYear)}</span>
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-700 font-medium">Tax saved to retirement</td>
                  <td className="py-3 text-right text-gray-600">£0</td>
                  <td className="py-3 text-right font-bold text-green-600">{fmt(cumulativeTaxSaved)}</td>
                  <td className="py-3 text-right">
                    <span className="text-green-600 font-bold">+{fmt(cumulativeTaxSaved)}</span>
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-700 font-medium">Mortgage interest saved</td>
                  <td className="py-3 text-right text-gray-600">£0</td>
                  <td className="py-3 text-right font-bold text-green-600">{fmt(mortgageInterestSaved)}</td>
                  <td className="py-3 text-right">
                    <span className="text-green-600 font-bold">+{fmt(mortgageInterestSaved)}</span>
                  </td>
                </tr>

                {/* Tier 2 rows — show data or placeholder */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-700 font-medium">Projected retirement age</td>
                  <td className="py-3 text-right text-gray-600">{hasDoNothing ? doNothingRetirementAge : '—'}</td>
                  <td className="py-3 text-right font-bold text-ma-navy">
                    {hasVoyantComparison ? s1RetirementAge : <span className="text-gray-400 text-xs italic font-normal">Confirmed after Voyant update</span>}
                  </td>
                  <td className="py-3 text-right">
                    {hasVoyantComparison ? (
                      retirementAgeDelta > 0 ? (
                        <span className="text-green-600 font-bold">{retirementAgeDelta} years forward ↑</span>
                      ) : retirementAgeDelta === 0 ? (
                        <span className="text-gray-500 text-xs italic">Unchanged</span>
                      ) : (
                        <span className="text-red-500 font-bold">{Math.abs(retirementAgeDelta)} years later</span>
                      )
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 text-gray-700 font-medium">Pot at freedom age</td>
                  <td className="py-3 text-right text-gray-600">{hasDoNothing ? fmt(doNothingPot) : '—'}</td>
                  <td className="py-3 text-right font-bold text-ma-navy">
                    {hasVoyantComparison ? fmt(s1Pot) : <span className="text-gray-400 text-xs italic font-normal">Confirmed after Voyant update</span>}
                  </td>
                  <td className="py-3 text-right">
                    {hasVoyantComparison ? (
                      <span className={potDelta >= 0 ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>
                        {potDelta >= 0 ? '+' : ''}{fmt(potDelta)}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                </tr>

                {/* Annual gap — Tier 2 */}
                {hasVoyantComparison && (
                  <tr>
                    <td className="py-3 text-gray-700 font-medium">Annual gap</td>
                    <td className="py-3 text-right text-gray-600">{fmt(doNothingAnnualSavings)}/yr</td>
                    <td className="py-3 text-right font-bold text-ma-navy">{fmt(s1AnnualSavings)}/yr</td>
                    <td className="py-3 text-right">
                      {lumpSumDelta > 0 ? (
                        <span className="text-green-600 font-bold">-{fmt(doNothingAnnualSavings - s1AnnualSavings)} ✓</span>
                      ) : (
                        <span className="text-gray-500">{fmt(doNothingAnnualSavings - s1AnnualSavings)}</span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Remaining gap + Session 2 target */}
        {s1Captured && s1AnnualSavings > 0 ? (
          <div className="border-2 border-amber-400 bg-amber-50 rounded-2xl p-6 mb-6"
               style={{ boxShadow: '0 0 20px rgba(184, 134, 11, 0.15)' }}>
            <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-3">Session 2 Target</h3>
            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-amber-900">{fmt(monthlyGap)}/month</div>
              <div className="text-sm text-amber-700 mt-1">
                in additional employer pension from your company — tax-deductible, CT-efficient.
              </div>
            </div>
            {businessMRR > 0 && (
              <div className="bg-amber-100 rounded-xl p-3 text-center">
                <span className="text-amber-800 text-sm">
                  That's <span className="font-bold">{(monthlyGap / businessMRR * 100).toFixed(1)}%</span> of your
                  current <span className="font-bold">{fmt(businessMRR)}/month</span> revenue.
                </span>
              </div>
            )}
          </div>
        ) : !s1Captured ? (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center mb-6">
            <p className="text-gray-500 text-sm italic">Complete Voyant update to reveal your S2 target.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
